// Навигационная панель приложения. Адаптируется под роли пользователя (студент, преподаватель, администратор).
// Содержит меню разделов, аватар с профилем и кнопку выхода из системы.

import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Nav, NavDropdown, Container, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import useAuthStore from '../../store/authStore';
import useUserStore from '../../store/userStore';

/** Возвращает инициалы из имени пользователя */
function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export default function AppNavbar() {
  const { user, logout } = useAuthStore();
  const { changePassword } = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();

  const isStaff = user?.roles?.some((r) => ['teacher', 'admin'].includes(r));
  const isAdmin = user?.roles?.includes('admin');
  const isStudent = user?.roles?.includes('student');
  const isOnlyStudent = isStudent && !isStaff;

  // Состояние модала смены пароля
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);

  const openChangePassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setChangeError('');
    setChangeSuccess(false);
    setShowChangePassword(true);
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      setChangeError('Новый пароль должен содержать минимум 6 символов');
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeError('Новый пароль и подтверждение не совпадают');
      return;
    }
    setChangingPassword(true);
    setChangeError('');
    try {
      await changePassword(currentPassword, newPassword);
      setChangeSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setChangeError(err?.response?.data?.detail ?? 'Ошибка при смене пароля');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
    <Navbar
      expand="md"
      className="ewl-navbar py-2"
      variant="dark"
    >
      <Container>
        {/* Бренд */}
        <Navbar.Brand as={Link} to="/dashboard" className="d-flex align-items-center gap-2">
          <span
            style={{
              width: '2rem',
              height: '2rem',
              borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #4f6ef7, #7c3aed)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            📖
          </span>
          <span>English We Love</span>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="main-navbar-nav" />

        <Navbar.Collapse id="main-navbar-nav">
          {/* Левое меню */}
          <Nav className="me-auto mt-2 mt-md-0">
            {/* Для студентов — выпадающее меню "Упражнения" */}
            {isStudent && (
              <NavDropdown
                title="Упражнения"
                id="exercises-dropdown"
                menuVariant="dark"
                active={location.pathname.startsWith('/student/exercises')}
              >
                <NavDropdown.Item as={Link} to="/student/exercises">
                  Все упражнения
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {/* Для студентов — "Мое обучение" */}
            {isStudent && (
              <NavDropdown
                title="Мое обучение"
                id="learning-dropdown"
                menuVariant="dark"
                active={location.pathname === '/student/statistics'}
              >
                <NavDropdown.Item as={Link} to="/student/statistics">
                  Моя статистика
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {isStaff && (
              <NavDropdown
                title="Преподаватель"
                id="teacher-dropdown"
                menuVariant="dark"
              >
                <NavDropdown.Item as={Link} to="/teacher/exercise-types">
                  Типы упражнений
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/teacher/exercises">
                  Упражнения
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item as={Link} to="/admin/students">
                  Студенты
                </NavDropdown.Item>
              </NavDropdown>
            )}

            {isAdmin && (
              <NavDropdown
                title="Администрирование"
                id="admin-dropdown"
                menuVariant="dark"
              >
                <NavDropdown.Item as={Link} to="/admin/users">
                  Пользователи
                </NavDropdown.Item>
                <NavDropdown.Item as={Link} to="/admin/complexity-levels">
                  Уровни сложности
                </NavDropdown.Item>
              </NavDropdown>
            )}
          </Nav>

          {/* Правое меню */}
          <Nav className="ms-auto align-items-center mt-2 mt-md-0 gap-2">
            {/* Гость — кнопки входа и регистрации */}
            {!user && (
              <>
                <Button
                  as={Link}
                  to="/login"
                  variant="outline-light"
                  size="sm"
                  style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8125rem', opacity: 0.85 }}
                >
                  Войти
                </Button>
                <Button
                  as={Link}
                  to="/register"
                  variant="light"
                  size="sm"
                  style={{ borderRadius: '0.4rem', fontWeight: 600, fontSize: '0.8125rem' }}
                >
                  Регистрация
                </Button>
              </>
            )}

            {/* Авторизованный пользователь */}
            {user && <NavDropdown
              align="end"
              menuVariant="dark"
              title={
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="ewl-avatar" title={user?.name}>
                    {getInitials(user?.name)}
                  </span>
                  <span
                    className="d-none d-sm-inline text-light"
                    style={{ fontSize: '0.875rem', opacity: 0.9, fontWeight: 500 }}
                  >
                    {user?.name}
                  </span>
                </span>
              }
              id="user-dropdown"
              style={{ '--bs-nav-link-padding-x': 0 }}
            >
              {isStudent && (
                <NavDropdown.Item as={Link} to="/student/profile">
                  Профиль
                </NavDropdown.Item>
              )}
              <NavDropdown.Item onClick={openChangePassword}>
                Сменить пароль
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item onClick={handleLogout} style={{ color: '#f87171' }}>
                Выйти
              </NavDropdown.Item>
            </NavDropdown>}

            {/* Явная кнопка «Выйти» — только для авторизованных */}
            {user && (
              <Button
                variant="outline-light"
                size="sm"
                onClick={handleLogout}
                style={{ borderRadius: '0.4rem', fontWeight: 500, fontSize: '0.8125rem', opacity: 0.85 }}
              >
                Выйти
              </Button>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>

    {/* ── Модальное окно смены пароля ── */}
    <Modal show={showChangePassword} onHide={() => setShowChangePassword(false)}>
      <Modal.Header closeButton>
        <div>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
            Сменить пароль
          </Modal.Title>
          <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
            {user?.name}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="p-4">
        {changeError && (
          <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
            {changeError}
          </Alert>
        )}
        {changeSuccess && (
          <Alert variant="success" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
            Пароль успешно изменён
          </Alert>
        )}

        {/* Поле текущего пароля */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
            Текущий пароль
          </Form.Label>
          <div className="position-relative">
            <Form.Control
              type={showCurrent ? 'text' : 'password'}
              placeholder="Введите текущий пароль"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={{ borderRadius: '0.4rem', paddingRight: '2.5rem' }}
              disabled={changeSuccess}
            />
            <button
              type="button"
              onClick={() => setShowCurrent((v) => !v)}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#6c757d',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              tabIndex={-1}
            >
              {showCurrent ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Form.Group>

        {/* Поле нового пароля */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
            Новый пароль
          </Form.Label>
          <div className="position-relative">
            <Form.Control
              type={showNew ? 'text' : 'password'}
              placeholder="Минимум 6 символов"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ borderRadius: '0.4rem', paddingRight: '2.5rem' }}
              disabled={changeSuccess}
            />
            <button
              type="button"
              onClick={() => setShowNew((v) => !v)}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#6c757d',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              tabIndex={-1}
            >
              {showNew ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Form.Group>

        {/* Поле подтверждения пароля */}
        <Form.Group className="mb-1">
          <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
            Подтверждение нового пароля
          </Form.Label>
          <div className="position-relative">
            <Form.Control
              type={showConfirm ? 'text' : 'password'}
              placeholder="Повторите новый пароль"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              isInvalid={confirmPassword.length > 0 && confirmPassword !== newPassword}
              style={{ borderRadius: '0.4rem', paddingRight: '2.5rem' }}
              disabled={changeSuccess}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              style={{
                position: 'absolute',
                right: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#6c757d',
                cursor: 'pointer',
                lineHeight: 1,
              }}
              tabIndex={-1}
            >
              {showConfirm ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
            <Form.Control.Feedback type="invalid" style={{ fontSize: '0.8rem' }}>
              Пароли не совпадают
            </Form.Control.Feedback>
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowChangePassword(false)}>
          {changeSuccess ? 'Закрыть' : 'Отмена'}
        </Button>
        {!changeSuccess && (
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
            style={{ fontWeight: 500 }}
          >
            {changingPassword ? <Spinner size="sm" animation="border" /> : 'Сменить пароль'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
    </>
  );
}
